import type { CatalogStatus } from '../types/models.js'

export type LifecycleAction = 'suspend' | 'activate' | 'archive'

export const LIFECYCLE_ACTIONS: Record<CatalogStatus, LifecycleAction[]> = {
  ACTIVE: ['suspend', 'archive'],
  SUSPENDED: ['activate', 'archive'],
  ARCHIVED: [],
}

export function availableLifecycleActions(status: CatalogStatus): LifecycleAction[] {
  return LIFECYCLE_ACTIONS[status] ?? []
}

export function isArchived(status: CatalogStatus): boolean {
  return status === 'ARCHIVED'
}

export type NameFieldErrorCode = 'required' | 'maxLength' | null

export function validateNameField(value: string, maxLength = 200): NameFieldErrorCode {
  if (!value.trim()) return 'required'
  if (value.trim().length > maxLength) return 'maxLength'
  return null
}

export type SortOrderErrorCode = 'invalidSortOrder' | null

export function validateSortOrderField(value: string): SortOrderErrorCode {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (!/^\d+$/.test(trimmed)) return 'invalidSortOrder'
  return null
}

export function normalizeSortOrder(value: string): number | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const n = Number(trimmed)
  return Number.isInteger(n) && n >= 0 ? n : undefined
}

export type PriceErrorCode = 'required' | 'invalid' | null

export function validatePriceField(value: string, pricePrecisionMode?: string): PriceErrorCode {
  const trimmed = value.trim()
  if (!trimmed) return 'required'
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return 'invalid'
  if (pricePrecisionMode === 'INTEGER_ONLY' && !Number.isInteger(n)) return 'invalid'
  if (pricePrecisionMode === 'HALF_UNIT' && Math.round(n * 100) % 50 !== 0) return 'invalid'
  return null
}

export type OptionalNumberErrorCode = 'invalid' | null

/**
 * Validates an optional numeric field (weight, calories, КБЖУ). Empty is valid
 * and means "no value". When `integer` is true the value must be whole (used for
 * calories, which the backend stores as INTEGER).
 */
export function validateOptionalNumber(value: string, integer = false): OptionalNumberErrorCode {
  const trimmed = value.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return 'invalid'
  if (integer && !Number.isInteger(n)) return 'invalid'
  return null
}

export function normalizeOptionalNumber(value: string, integer = false): number | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return undefined
  if (integer && !Number.isInteger(n)) return undefined
  return n
}

export function normalizePrice(value: string): number | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const n = Number(trimmed)
  return Number.isFinite(n) && n >= 0 ? n : undefined
}

export function formatPrice(price: number): string {
  return `${price} ₽`
}

export function formatPriceRange(min: number, max: number): string {
  if (min === max) return formatPrice(min)
  return `${min}–${max} ₽`
}

export type PositiveIntErrorCode = 'required' | 'invalid' | null

export function validatePositiveIntField(value: string): PositiveIntErrorCode {
  const trimmed = value.trim()
  if (!trimmed) return 'required'
  const n = Number(trimmed)
  if (!Number.isInteger(n) || n < 1) return 'invalid'
  return null
}

export function normalizePositiveInt(value: string): number | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const n = Number(trimmed)
  return Number.isInteger(n) && n >= 1 ? n : undefined
}

export interface GroupSelectionErrors {
  min?: string
  max?: string
}

/**
 * Client-side mirror of the backend ModifierGroup selection invariants.
 * Returns error codes mapped to i18n keys by the form:
 *   min: 'invalid' | 'requiredMin'
 *   max: 'required' | 'invalid' | 'maxLtMin' | 'requiredMaxZero'
 * `unlimited === true` means `max` is null (no upper bound).
 */
export function validateModifierGroupSelection(
  minValue: string,
  maxValue: string,
  unlimited: boolean,
  isRequired: boolean,
): GroupSelectionErrors {
  const errors: GroupSelectionErrors = {}

  const minOk = minValue.trim() === '' || (Number.isInteger(Number(minValue)) && Number(minValue) >= 0)
  const min = minValue.trim() === '' ? 0 : Number(minValue)
  if (!minOk) errors.min = 'invalid'

  let max: number | null = null
  if (!unlimited) {
    const trimmed = maxValue.trim()
    if (trimmed === '') {
      errors.max = 'required'
    } else if (!Number.isInteger(Number(trimmed)) || Number(trimmed) < 0) {
      errors.max = 'invalid'
    } else {
      max = Number(trimmed)
    }
  }

  if (minOk && max !== null && max < min) errors.max = 'maxLtMin'
  if (isRequired && minOk && min < 1) errors.min = 'requiredMin'
  if (isRequired && max === 0) errors.max = 'requiredMaxZero'

  return errors
}